// frontend/src/layouts/admin/exams/ViewSamplePage.js
// Admin "View Sample" page — identical layout to the annotator annotation page,
// but all fields are read-only and Save/Submit actions are replaced with "Add Remark".

import { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CommentIcon from "@mui/icons-material/Comment";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import WaveformPlayer from "components/WaveformPlayer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { getExamBundle, getAnnotation, saveRemark } from "services/sampleService";

// ─── helpers (mirrored from annotation.js) ───────────────────────────────────

const useQuery = () => new URLSearchParams(useLocation().search);

const readCorrectOptions = ["Yes", "No"];
const bgNoiseOptions = ["Motor Bike", "Machine Noise", "Animals", "Other"];
const notTranscribableOptions = ["Blank Audio", "Too Noisy", "Language Mismatch", "Other"];

const fixUTF8Encoding = (text) => {
  if (!text) return text;
  try {
    if (/Ã[±³©­]/g.test(text)) {
      return text
        .replace(/Ã±/g, "ñ")
        .replace(/Ã³/g, "ó")
        .replace(/Ã©/g, "é")
        .replace(/Ã­/g, "í")
        .replace(/Ã¡/g, "á")
        .replace(/Ãº/g, "ú");
    }
    return text;
  } catch (e) {
    return text;
  }
};

const splitTextIntoLines = (text, isPassage = false) => {
  if (text == null) return [""];
  const fixedText = fixUTF8Encoding(text);
  const strText = String(fixedText).trim();
  if (strText === "") return [""];

  let lines = strText.split("\n").map((l) => l.trim());
  if (!isPassage) return lines;

  const title = lines[0] || "";
  let passageLineIndex = 1;
  if (lines[1] === "") passageLineIndex = 2;
  const passageText = lines.slice(passageLineIndex).join(" ").trim();
  if (!passageText) return [title];

  const sentences = passageText
    .match(/[^.]+\.?/g)
    .map((s) => s.trim())
    .filter(Boolean);
  return [title, ...sentences];
};

// ─── SelectField (read-only for admin — always disabled) ─────────────────────

const SelectField = ({ field, options, defaultValue, selectedValue }) => {
  const [openDropdown, setOpenDropdown] = useState(false);
  return (
    <FormControl fullWidth sx={{ height: 50 }}>
      <Select
        labelId={`${field}-label`}
        value={selectedValue ?? defaultValue}
        onOpen={() => setOpenDropdown(true)}
        onClose={() => setOpenDropdown(false)}
        IconComponent={() => <FontAwesomeIcon icon={openDropdown ? faChevronUp : faChevronDown} />}
        disabled
        sx={{
          height: 50,
          fontSize: "0.8rem",
          "& svg": {
            top: "calc(50% - 0.5em)",
            right: 10,
            position: "absolute",
            pointerEvents: "none",
          },
        }}
      >
        <MenuItem value={defaultValue} disabled>
          {defaultValue}
        </MenuItem>
        {options.map((opt, i) => (
          <MenuItem key={i} value={opt}>
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

SelectField.propTypes = {
  field: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  defaultValue: PropTypes.string.isRequired,
  selectedValue: PropTypes.string,
};

// ─── Header row (same as annotation.js, Actions column → Remarks) ────────────

const HeaderRow = ({ showDisfluency, showMistakes }) => (
  <Grid container spacing={2} alignItems="center" py={1} borderBottom="1px solid #e6e6e6">
    <Grid item xs={2}>
      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          Recording
        </Typography>
        <Tooltip title="Audio for this sample" placement="top">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>
    </Grid>
    <Grid item xs={2}>
      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          Canonical Text
        </Typography>
        <Tooltip title="Expected/reference text" placement="top">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>
    </Grid>
    <Grid item xs={2}>
      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          Transcribed Text
        </Typography>
        <Tooltip title="Annotator's transcription" placement="top">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>
    </Grid>
    <Grid item xs={1}>
      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          Read Correctly?
        </Typography>
        <Tooltip title="Was pronunciation correct?" placement="top">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>
    </Grid>
    {showMistakes && (
      <Grid item xs={1}>
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            No. of mistakes
          </Typography>
        </Box>
      </Grid>
    )}
    <Grid item xs={1}>
      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          BG Noise
        </Typography>
      </Box>
    </Grid>
    <Grid item xs={1.5}>
      <Box display="flex" alignItems="center" gap={1} justifyContent="center">
        <Typography fontWeight="bold" fontSize="0.875rem">
          Non-Transcribable
        </Typography>
      </Box>
    </Grid>
    {showDisfluency && (
      <Grid item xs={0.75}>
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            Disfluency Occurences
          </Typography>
        </Box>
      </Grid>
    )}
    <Grid item xs={1.5}>
      <Typography fontWeight="bold" align="center" fontSize="0.875rem">
        Add Remark
      </Typography>
    </Grid>
  </Grid>
);

HeaderRow.propTypes = {
  showDisfluency: PropTypes.bool,
  showMistakes: PropTypes.bool,
};

// ─── Annotation View Row (read-only version of AnnotationRow) ─────────────────

const AnnotationViewRow = ({ row, onAddRemark }) => {
  const isPassage = row?.is_passage === 1 || row?.is_passage === "1";

  const canonicalLines = useMemo(
    () => splitTextIntoLines(row.sample_text, isPassage),
    [row.sample_text, isPassage]
  );

  const transcriptionLines = useMemo(() => {
    const lines = row.annotation?.transcription_lines || [];
    const diff = canonicalLines.length - lines.length;
    if (diff > 0) return [...lines, ...Array(diff).fill("")];
    return lines.slice(0, canonicalLines.length);
  }, [row.annotation, canonicalLines]);

  /* ── PASSAGE LAYOUT ── */
  if (isPassage) {
    return (
      <Box py={2} borderBottom="1px solid #eee">
        <Box mb={2}>
          <WaveformPlayer src={row.recording_file} />
        </Box>

        {canonicalLines.map((canonicalLine, lineIndex) => (
          <Grid container spacing={2} key={lineIndex} alignItems="flex-start" sx={{ mb: 1.5 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                value={canonicalLine}
                size="small"
                multiline
                InputProps={{ readOnly: true }}
                sx={{ bgcolor: "#f7f8f9" }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                value={
                  transcriptionLines[lineIndex] === "-" ? "" : transcriptionLines[lineIndex] || ""
                }
                size="small"
                multiline
                placeholder={`Sentence ${lineIndex + 1}`}
                disabled
              />
            </Grid>
          </Grid>
        ))}

        {/* Meta fields */}
        <Grid container spacing={2} mt={2} alignItems="center">
          <Grid item xs={3}>
            <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
              Read Correctly
            </Typography>
            <SelectField
              field="read_correctly"
              options={readCorrectOptions}
              defaultValue="Select"
              selectedValue={row.annotation?.read_correctly ? "Yes" : "No"}
            />
          </Grid>
          <Grid item xs={3}>
            <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
              BG Noise
            </Typography>
            <SelectField
              field="bg_noise"
              options={bgNoiseOptions}
              defaultValue="Select"
              selectedValue={row.annotation?.bg_noise || "Select"}
            />
          </Grid>
          <Grid item xs={3}>
            <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
              Non-Transcribable
            </Typography>
            <SelectField
              field="not_transcribable"
              options={notTranscribableOptions}
              defaultValue="Select Reason"
              selectedValue={row.annotation?.not_transcribable || "Select Reason"}
            />
          </Grid>
          {/* Add Remark button (replaces Save) */}
          <Grid item xs={1}>
            <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
              &nbsp;
            </Typography>
            <MDButton
              size="small"
              color="info"
              variant="outlined"
              startIcon={<StickyNote2Icon fontSize="small" />}
              onClick={() => onAddRemark(row)}
              sx={{ whiteSpace: "nowrap" }}
            >
              Add Remark
            </MDButton>
          </Grid>
        </Grid>
      </Box>
    );
  }

  /* ── NON-PASSAGE (TABLE) LAYOUT ── */
  return (
    <Grid container spacing={2} py={1} borderBottom="1px solid #f0f0f0">
      {/* Recording */}
      <Grid item xs={3}>
        <WaveformPlayer src={row.recording_file} />
      </Grid>

      {/* Canonical */}
      <Grid item xs={2}>
        {canonicalLines.map((canonicalLine, lineIndex) => (
          <Box key={lineIndex} sx={{ mb: 0.5 }}>
            <TextField
              value={canonicalLine}
              fullWidth
              size="small"
              multiline
              InputProps={{ readOnly: true }}
              sx={{ bgcolor: "#f7f8f9" }}
            />
          </Box>
        ))}
      </Grid>

      {/* Transcription */}
      <Grid item xs={2}>
        {transcriptionLines.map((line, lineIndex) => (
          <TextField
            key={lineIndex}
            value={line}
            fullWidth
            size="small"
            multiline
            disabled
            sx={{ mb: 0.5 }}
          />
        ))}
      </Grid>

      {/* Read Correctly */}
      <Grid item xs={1}>
        <SelectField
          field="read_correctly"
          options={readCorrectOptions}
          defaultValue="Select Correct"
          selectedValue={row.annotation?.read_correctly ? "Yes" : "No"}
        />
      </Grid>

      {/* No. of mistakes (conditional) */}
      {String(row.is_no_mistakes ?? "0") === "1" && (
        <Grid item xs={1}>
          <TextField
            fullWidth
            size="small"
            type="number"
            value={row.annotation?.no_of_mistakes ?? 0}
            disabled
          />
        </Grid>
      )}

      {/* BG Noise */}
      <Grid item xs={1}>
        <SelectField
          field="bg_noise"
          options={bgNoiseOptions}
          defaultValue="Select"
          selectedValue={row.annotation?.bg_noise || "Select"}
        />
      </Grid>

      {/* Non-Transcribable */}
      <Grid item xs={1.5}>
        <SelectField
          field="not_transcribable"
          options={notTranscribableOptions}
          defaultValue="Select Reason"
          selectedValue={row.annotation?.not_transcribable || "Select Reason"}
        />
      </Grid>

      {/* Disfluency (conditional) */}
      {String(row.is_disfluency ?? "0") === "1" && (
        <Grid item xs={0.75}>
          <TextField
            fullWidth
            size="small"
            type="number"
            value={row.annotation?.disfluency_occurences ?? 0}
            disabled
          />
        </Grid>
      )}

      {/* Add Remark (replaces Save icon) */}
      <Grid item xs={1.5} sx={{ display: "flex", alignItems: "center" }}>
        <MDButton
          size="small"
          color="info"
          variant="outlined"
          startIcon={<StickyNote2Icon fontSize="small" />}
          onClick={() => onAddRemark(row)}
          sx={{ whiteSpace: "nowrap" }}
        >
          Add Remark
        </MDButton>
      </Grid>
    </Grid>
  );
};

AnnotationViewRow.propTypes = {
  row: PropTypes.shape({
    recording_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recording_file: PropTypes.string,
    sample_text: PropTypes.string,
    is_passage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    is_disfluency: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    is_no_mistakes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    annotation: PropTypes.shape({
      transcription_lines: PropTypes.arrayOf(PropTypes.string),
      read_correctly: PropTypes.bool,
      bg_noise: PropTypes.string,
      not_transcribable: PropTypes.string,
      disfluency_occurences: PropTypes.number,
      no_of_mistakes: PropTypes.number,
    }),
  }).isRequired,
  onAddRemark: PropTypes.func.isRequired,
};

// ─── Main ViewSamplePage ──────────────────────────────────────────────────────

export default function ViewSamplePage() {
  const query = useQuery();
  const navigate = useNavigate();
  const examId = query.get("exam_id") || "";
  // Which annotator's annotations to show ("1" or "2"). Defaults to "1".
  const annotatorSlot = query.get("annotator") === "2" ? 2 : 1;

  const [loading, setLoading] = useState(false);
  const [exam, setExam] = useState(null);
  const [sampleTypes, setSampleTypes] = useState([]);
  const [activeType, setActiveType] = useState("");
  const [groupedRecordings, setGroupedRecordings] = useState({});

  // Add Remark dialog
  const [remarkDialogOpen, setRemarkDialogOpen] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [remarkText, setRemarkText] = useState("");
  const [savingRemark, setSavingRemark] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

  const showSnackbar = useCallback(({ severity = "success", message = "" }) => {
    setSnackbar({ open: true, severity, message });
    setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 3000);
  }, []);

  // ── Load bundle + chosen annotator's saved annotations ──────────────────
  const processBundle = useCallback(
    async (bundle) => {
      const chosenAnnotatorId =
        annotatorSlot === 2 ? bundle.exam?.annotator2_id : bundle.exam?.annotator1_id;
      const newGrouped = {};

      for (const sampleType of bundle.sample_types || []) {
        const groups = bundle.tabs?.[sampleType] || [];
        const recordingsWithText = groups.flatMap((group) =>
          (group.recordings || []).map((rec) => {
            const isPassage = rec.is_passage === 1 || rec.is_passage === "1";
            const canonicalLines = splitTextIntoLines(group.sample_text, isPassage);
            return {
              ...rec,
              is_disfluency: rec.is_disfluency ?? group.is_disfluency ?? 0,
              is_no_mistakes: rec.is_no_mistakes ?? group.is_no_mistakes ?? 1,
              is_passage: rec.is_passage ?? group.is_passage ?? 0,
              sample_text: group.sample_text,
              sample_type: sampleType,
              annotation: {
                transcription_lines: canonicalLines.map(() => ""),
                read_correctly: false,
                bg_noise: "",
                not_transcribable: "",
                disfluency_occurences: 0,
                no_of_mistakes: 0,
              },
            };
          })
        );

        // Load chosen annotator's saved annotations (round 1)
        const annotationPromises = recordingsWithText.map(async (rec) => {
          if (!chosenAnnotatorId) return rec;
          try {
            const result = await getAnnotation(rec.recording_id, chosenAnnotatorId, 1);
            if (result?.status === "success" && result.data) {
              const d = result.data;
              const isPassage = rec.is_passage === 1 || rec.is_passage === "1";
              const canonicalLines = splitTextIntoLines(rec.sample_text, isPassage);
              let transcriptionLines = [];
              if (d.transcription_text && d.transcription_text.trim() !== "") {
                transcriptionLines = d.transcription_text.split("\n").map((l) => l.trim());
              } else {
                transcriptionLines = Array(canonicalLines.length).fill("");
              }
              // Align line count with canonical
              if (transcriptionLines.length < canonicalLines.length) {
                transcriptionLines = [
                  ...transcriptionLines,
                  ...Array(canonicalLines.length - transcriptionLines.length).fill(""),
                ];
              } else {
                transcriptionLines = transcriptionLines.slice(0, canonicalLines.length);
              }
              return {
                ...rec,
                annotation: {
                  transcription_lines: transcriptionLines,
                  read_correctly: Boolean(d.read_correctly),
                  bg_noise: d.bg_noise ?? "",
                  not_transcribable: d.not_transcribable ?? "",
                  disfluency_occurences: d.disfluency_occurences ?? 0,
                  no_of_mistakes: d.no_of_mistakes ?? 0,
                },
              };
            }
          } catch {
            /* annotation not yet saved — show empty */
          }
          return rec;
        });

        newGrouped[sampleType] = await Promise.all(annotationPromises);
      }
      setGroupedRecordings(newGrouped);
    },
    [annotatorSlot]
  );

  useEffect(() => {
    if (!examId) return;
    (async () => {
      try {
        setLoading(true);
        const bundle = await getExamBundle(examId);
        setExam(bundle.exam || null);
        setSampleTypes(bundle.sample_types || []);
        setActiveType(bundle.sample_types?.[0] || "");
        await processBundle(bundle);
      } catch (err) {
        console.error("ViewSamplePage: failed to load exam bundle", err);
        showSnackbar({ severity: "error", message: "Failed to load exam data" });
      } finally {
        setLoading(false);
      }
    })();
  }, [examId, processBundle, showSnackbar]);

  const activeRecordings = useMemo(
    () => groupedRecordings[activeType] || [],
    [groupedRecordings, activeType]
  );

  const hasAnyPassage = useMemo(
    () => activeRecordings.some((r) => r?.is_passage === 1 || r?.is_passage === "1"),
    [activeRecordings]
  );
  const hasDisfluency = useMemo(
    () => activeRecordings.some((r) => String(r?.is_disfluency ?? 0) === "1"),
    [activeRecordings]
  );
  const hasNoMistakes = useMemo(
    () => activeRecordings.some((r) => String(r?.is_no_mistakes ?? 0) === "1"),
    [activeRecordings]
  );

  // Tab navigation
  const currentIndex = sampleTypes.indexOf(activeType);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < sampleTypes.length - 1;

  const handlePreviousTab = () => {
    if (hasPrevious) setActiveType(sampleTypes[currentIndex - 1]);
  };
  const handleNextTab = () => {
    if (hasNext) setActiveType(sampleTypes[currentIndex + 1]);
  };

  // Section status (same logic as annotation.js)
  const getSectionStatus = (sampleType) => {
    const recs = groupedRecordings[sampleType] || [];
    if (!recs.length) return "pending";
    const lower = (s) => String(s || "").toLowerCase();
    const allBothSubmitted = recs.every(
      (r) =>
        lower(r.annotation1_status) === "submitted" && lower(r.annotation2_status) === "submitted"
    );
    if (allBothSubmitted) return "completed";
    const anySubmitted = recs.some(
      (r) =>
        lower(r.annotation1_status) === "submitted" || lower(r.annotation2_status) === "submitted"
    );
    if (anySubmitted) return "inProgress";
    return "pending";
  };

  // Remark handlers
  const handleAddRemark = (rec) => {
    setSelectedRecording(rec);
    setRemarkText("");
    setRemarkDialogOpen(true);
  };

  const handleSaveRemark = async () => {
    const recordingId = selectedRecording?.id ?? selectedRecording?.recording_id;
    try {
      setSavingRemark(true);
      await saveRemark(recordingId, remarkText.trim());
      setRemarkDialogOpen(false);
      showSnackbar({ severity: "success", message: "Remark saved successfully" });
    } catch (err) {
      showSnackbar({ severity: "error", message: err.message || "Failed to save remark" });
    } finally {
      setSavingRemark(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box p={3} bgcolor="#fafafa" borderRadius={2} border="1px solid #ddd">
              {/* ── Header card (mirrors annotation.js) ── */}
              <Card sx={{ p: 2, mb: 3, position: "relative" }}>
                <Grid container spacing={2} alignItems="center">
                  {/* Back button */}
                  <Grid item xs={1}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => navigate("/admin/exams")}
                      sx={{
                        width: "100%",
                        height: 50,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        borderRadius: "10px",
                      }}
                    >
                      Back
                    </MDButton>
                  </Grid>

                  {/* Session info */}
                  <Grid item xs={10}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box mt={1}>
                          <Typography variant="body2">Session ID: {examId || "N/A"}</Typography>
                          {exam?.annotator1_name && (
                            <Typography variant="caption" color="text.secondary">
                              Annotator 1: {exam.annotator1_name}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>

              <>
                {/* ── Sample type tabs (identical to annotation.js) ── */}
                <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                  <Typography variant="h6" mb={1} fontWeight="bold" color="#333">
                    Verify Samples :
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
                    {sampleTypes.map((label, i) => {
                      const status = getSectionStatus(label);
                      const isActive = label === activeType;
                      return (
                        <Button
                          key={i}
                          onClick={() => setActiveType(label)}
                          variant="outlined"
                          sx={{
                            borderRadius: 1,
                            boxShadow: isActive
                              ? "0 4px 0 0 blue"
                              : status === "completed"
                              ? "0 4px 0 0 green"
                              : "0 4px 0 0 gray",
                            border: "1px solid #ccc",
                            minWidth: 100,
                            color: "#000",
                            textTransform: "none",
                            fontSize: "15px",
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>

                {/* ── Annotation grid (identical styling to annotation.js) ── */}
                <Box
                  bgcolor="#fff"
                  px={2}
                  py={0}
                  sx={{
                    boxShadow: 4,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid #e0e0e0",
                    mt: 2,
                  }}
                >
                  {!hasAnyPassage && (
                    <HeaderRow showDisfluency={hasDisfluency} showMistakes={hasNoMistakes} />
                  )}

                  {activeRecordings.map((row) => (
                    <AnnotationViewRow
                      key={row.recording_id}
                      row={row}
                      onAddRemark={handleAddRemark}
                    />
                  ))}

                  {activeRecordings.length === 0 && (
                    <Box py={4} textAlign="center">
                      <Typography variant="body2" color="text.secondary">
                        No recordings found for this sample type.
                      </Typography>
                    </Box>
                  )}

                  {/* ── Footer: only tab navigation, no Save/Submit ── */}
                  <Box py={3} display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Button
                        variant="contained"
                        startIcon={<NavigateBeforeIcon />}
                        onClick={handlePreviousTab}
                        disabled={!hasPrevious}
                        sx={{
                          fontWeight: "700",
                          padding: "10px 26px",
                          textTransform: "none",
                          fontSize: "0.95rem",
                          borderRadius: "10px",
                          background: hasPrevious
                            ? "linear-gradient(180deg,#20c997 0%,#17a589 100%)"
                            : "#f0f0f0",
                          color: hasPrevious ? "#fff" : "#9aa0a6",
                          boxShadow: hasPrevious ? "0 6px 14px rgba(32,201,151,0.18)" : "none",
                          transition: "all 180ms ease",
                          "&:hover": {
                            background: hasPrevious
                              ? "linear-gradient(180deg,#17a589 0%,#139077 100%)"
                              : "#f0f0f0",
                          },
                          "&.Mui-disabled": { opacity: 0.6, pointerEvents: "none" },
                        }}
                      >
                        Previous
                      </Button>
                    </Box>

                    <Box>
                      {hasNext && (
                        <Button
                          variant="contained"
                          endIcon={<NavigateNextIcon />}
                          onClick={handleNextTab}
                          sx={{
                            fontWeight: "700",
                            padding: "10px 26px",
                            textTransform: "none",
                            fontSize: "0.95rem",
                            borderRadius: "10px",
                            background: "linear-gradient(180deg,#1d57dd 0%,#1648b8 100%)",
                            color: "#fff",
                            boxShadow: "0 6px 18px rgba(29,87,221,0.18)",
                            transition: "all 180ms ease",
                            "&:hover": {
                              background: "linear-gradient(180deg,#1648b8 0%,#123a94 100%)",
                            },
                          }}
                        >
                          Next
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              </>
            </Box>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* ── Add Remark Dialog ── */}
      <Dialog
        open={remarkDialogOpen}
        onClose={() => setRemarkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}
        >
          <Typography variant="h6" fontWeight={700}>
            Add Remark
          </Typography>
          <IconButton size="small" onClick={() => setRemarkDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Remark"
            placeholder="Enter your remark here..."
            value={remarkText}
            onChange={(e) => setRemarkText(e.target.value)}
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setRemarkDialogOpen(false)}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSaveRemark}
            disabled={!remarkText.trim() || savingRemark}
          >
            {savingRemark ? "Saving..." : "Save Remark"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
